import { useEffect, useMemo, useRef, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout';
import type { CartItem } from '../../store/usePosStore';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import { buildEvaluatedLineMap, formatPromoMoney } from '../../../promotions/public-api';
import { POS_MOBILE_CART_COMPACT_LAYOUT } from '../../application/posLayout.constants';
import { resolveCheckoutHeroPromoDisplay } from '../checkout/posCheckoutDerived';

export type UsePosCartSectionLayoutInput = {
  cart: CartItem[];
  discountTotal?: number;
  cartEvaluation?: CartEvaluationResult | null;
  inSheet: boolean;
  linesScrollMaxHeightProp?: number;
};

export type UsePosCartSectionLayoutResult = {
  isCompactCart: boolean;
  useMobileCartCompact: boolean;
  useCompactRows: boolean;
  cartContentWidth: number;
  setCartContentWidth: (w: number) => void;
  cartLinesExpanded: boolean;
  setCartLinesExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  showCartCollapse: boolean;
  cartLinesCollapsed: boolean;
  linesScrollMaxHeight: number;
  evaluatedByLineId: ReturnType<typeof buildEvaluatedLineMap>;
  itemCountLabel: string;
  cartBadgePulse: boolean;
  savingsLabel: string | null;
  hasDiscounts: boolean;
};

export function usePosCartSectionLayout({
  cart,
  discountTotal,
  cartEvaluation,
  inSheet,
  linesScrollMaxHeightProp,
}: UsePosCartSectionLayoutInput): UsePosCartSectionLayoutResult {
  const { height } = useWindowDimensions();
  const { layoutWide: isWideLayout } = useTabWorkspaceLayout();
  const isCompactCart = !isWideLayout;
  const useMobileCartCompact = isCompactCart && POS_MOBILE_CART_COMPACT_LAYOUT && !inSheet;
  const [cartContentWidth, setCartContentWidth] = useState(0);
  const useCompactRows = (isCompactCart && !inSheet) || cartContentWidth < 420;
  const [cartLinesExpanded, setCartLinesExpanded] = useState(inSheet || !useMobileCartCompact);
  const showCartCollapse = inSheet
    ? false
    : useMobileCartCompact
      ? cart.length >= 1
      : isCompactCart && cart.length > 4;
  const cartLinesCollapsed = showCartCollapse && !cartLinesExpanded;
  const evaluatedByLineId = useMemo(() => buildEvaluatedLineMap(cartEvaluation), [cartEvaluation]);

  const hasDiscounts = discountTotal != null && discountTotal > 0;
  const promoCount = useMemo(() => {
    const display = resolveCheckoutHeroPromoDisplay(cartEvaluation);
    return display.kind === 'items' ? display.items.length : 0;
  }, [cartEvaluation]);

  const savingsLabel = useMemo(() => {
    if (!hasDiscounts || discountTotal == null || promoCount === 0) return null;
    const amount = formatPromoMoney(discountTotal);
    if (promoCount === 1 && cartEvaluation) {
      const all = [
        ...(cartEvaluation.cartDiscounts ?? []),
        ...(cartEvaluation.lines ?? []).flatMap((l) => l.appliedDiscounts ?? []),
      ];
      const name = all[0]?.promotionName?.trim();
      if (name) return `${amount} · ${name}`;
    }
    const promoWord = promoCount === 1 ? 'promo' : 'promos';
    return `${amount} en ${promoCount} ${promoWord}`;
  }, [hasDiscounts, discountTotal, promoCount]);

  const linesScrollMaxHeight = useMemo(() => {
    if (linesScrollMaxHeightProp != null) {
      return linesScrollMaxHeightProp;
    }
    if (isWideLayout) {
      return Math.min(height * 0.46, 420);
    }
    if (inSheet) {
      return Math.min(Math.max(height * 0.42, 160), 360);
    }
    if (POS_MOBILE_CART_COMPACT_LAYOUT) {
      return Math.min(Math.max(height * 0.22, 100), 180);
    }
    return Math.min(Math.max(height * 0.34, 160), 280);
  }, [height, inSheet, isWideLayout, linesScrollMaxHeightProp]);

  const itemCountLabel = cart.length === 1 ? '1 ítem' : `${cart.length} ítems`;
  const prevCartCountRef = useRef(cart.length);
  const [cartBadgePulse, setCartBadgePulse] = useState(false);

  useEffect(() => {
    if (cart.length > prevCartCountRef.current) {
      setCartBadgePulse(true);
      const timer = setTimeout(() => setCartBadgePulse(false), 520);
      prevCartCountRef.current = cart.length;
      return () => clearTimeout(timer);
    }
    prevCartCountRef.current = cart.length;
  }, [cart.length]);

  return {
    isCompactCart,
    useMobileCartCompact,
    useCompactRows,
    cartContentWidth,
    setCartContentWidth,
    cartLinesExpanded,
    setCartLinesExpanded,
    showCartCollapse,
    cartLinesCollapsed,
    linesScrollMaxHeight,
    evaluatedByLineId,
    itemCountLabel,
    cartBadgePulse,
    savingsLabel,
    hasDiscounts,
  };
}
